import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import companiesService, { 
  Company, 
  CompanyCreate, 
  CompanyUpdate, 
  CompanyFilters,
  AgencyClientRelation,
  CompanyUserRelation
} from './companiesService';

interface CompaniesState {
  companies: Company[];
  selectedCompany: Company | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  limit: number;
  filters: CompanyFilters;
  viewMode: 'list' | 'grid';
  isLoadingClients: boolean;
  clients: any[];
}

const initialState: CompaniesState = {
  companies: [],
  selectedCompany: null,
  isLoading: false,
  error: null,
  totalCount: 0,
  page: 1,
  limit: 10,
  filters: {},
  viewMode: 'list',
  isLoadingClients: false,
  clients: []
};

// Şirketleri getir
export const getCompanies = createAsyncThunk(
  'companies/getCompanies',
  async (params: CompanyFilters, { rejectWithValue }) => {
    try {
      const response = await companiesService.getCompanies(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch companies');
    }
  }
);

// Şirket detaylarını getir
export const getCompany = createAsyncThunk(
  'companies/getCompany',
  async (id: number, { rejectWithValue }) => {
    try {
      const company = await companiesService.getCompany(id);
      return company;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch company details');
    }
  }
);

// Yeni şirket oluştur
export const createCompany = createAsyncThunk(
  'companies/createCompany',
  async (companyData: CompanyCreate, { rejectWithValue }) => {
    try {
      const company = await companiesService.createCompany(companyData);
      return company;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create company');
    }
  }
);

// Şirket bilgilerini güncelle
export const updateCompany = createAsyncThunk(
  'companies/updateCompany',
  async ({ companyId, companyData }: { companyId: number, companyData: CompanyUpdate }, { rejectWithValue }) => {
    try {
      const company = await companiesService.updateCompany(companyId, companyData);
      return company;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update company');
    }
  }
);

// Şirketi sil
export const deleteCompany = createAsyncThunk(
  'companies/deleteCompany',
  async (id: number, { rejectWithValue }) => {
    try {
      await companiesService.deleteCompany(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete company');
    }
  }
);

// Şirket kullanıcılarını getir
export const getCompanyUsers = createAsyncThunk(
  'companies/getCompanyUsers',
  async (companyId: number, { rejectWithValue }) => {
    try {
      const users = await companiesService.getCompanyUsers(companyId);
      return { companyId, users };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch company users');
    }
  }
);

// Şirkete kullanıcı ekle
export const addUsersToCompany = createAsyncThunk(
  'companies/addUsersToCompany',
  async ({ companyId, userRelation }: { companyId: number, userRelation: CompanyUserRelation }, { rejectWithValue }) => {
    try {
      await companiesService.addUsersToCompany(companyId, userRelation);
      return { companyId, userIds: userRelation.user_ids };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to add users to company');
    }
  }
);

// Şirketten kullanıcı çıkar
export const removeUsersFromCompany = createAsyncThunk(
  'companies/removeUsersFromCompany',
  async ({ companyId, userRelation }: { companyId: number, userRelation: CompanyUserRelation }, { rejectWithValue }) => {
    try {
      await companiesService.removeUsersFromCompany(companyId, userRelation);
      return { companyId, userIds: userRelation.user_ids };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to remove users from company');
    }
  }
);

// Ajans-müşteri ilişkisi ekle
export const addAgencyClientRelation = createAsyncThunk(
  'companies/addAgencyClientRelation',
  async (relation: AgencyClientRelation, { rejectWithValue }) => {
    try {
      await companiesService.addAgencyClientRelation(relation);
      return relation;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to add agency-client relation');
    }
  }
);

// Ajans-müşteri ilişkisini kaldır
export const removeAgencyClientRelation = createAsyncThunk(
  'companies/removeAgencyClientRelation',
  async (relation: AgencyClientRelation, { rejectWithValue }) => {
    try {
      await companiesService.removeAgencyClientRelation(relation);
      return relation;
    } catch (error: any) {
      // Handle different types of error responses
      let errorMessage = 'Failed to remove agency-client relation';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Yeni bir thunk ekleyelim - belirli bir agency ID'ye göre client'ları getirmek için
export const getClientsByAgencyId = createAsyncThunk(
  'companies/getClientsByAgencyId',
  async (agencyId: number, { rejectWithValue }) => {
    try {
      const response = await companiesService.getClientsByAgencyId(agencyId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setFilters: (state, action: PayloadAction<CompanyFilters>) => {
      state.filters = action.payload;
    },
    setViewMode: (state, action: PayloadAction<'list' | 'grid'>) => {
      state.viewMode = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedCompany: (state) => {
      state.selectedCompany = null;
    },
    clearClients: (state) => {
      state.clients = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // getCompanies
      .addCase(getCompanies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies = action.payload.companies;
        state.totalCount = action.payload.total;
      })
      .addCase(getCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // getCompany
      .addCase(getCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedCompany = action.payload;
      })
      .addCase(getCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // createCompany
      .addCase(createCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateCompany
      .addCase(updateCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.companies.findIndex(company => company.id === action.payload.id);
        if (index !== -1) {
          state.companies[index] = action.payload;
        }
        if (state.selectedCompany?.id === action.payload.id) {
          state.selectedCompany = action.payload;
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // deleteCompany
      .addCase(deleteCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies = state.companies.filter(company => company.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedCompany?.id === action.payload) {
          state.selectedCompany = null;
        }
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Client'ları getirme işlemleri
      .addCase(getClientsByAgencyId.pending, (state) => {
        state.isLoadingClients = true;
        state.error = null;
      })
      .addCase(getClientsByAgencyId.fulfilled, (state, action) => {
        state.isLoadingClients = false;
        state.clients = action.payload;
      })
      .addCase(getClientsByAgencyId.rejected, (state, action) => {
        state.isLoadingClients = false;
        state.error = action.payload as string;
      })
      
      // addAgencyClientRelation
      .addCase(addAgencyClientRelation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addAgencyClientRelation.fulfilled, (state, action) => {
        state.isLoading = false;
        // Agency company'yi güncelle
        const agency = state.companies.find(company => company.id === action.payload.agency_id);
        if (agency && !agency.managed_companies?.includes(action.payload.managed_company_id)) {
          agency.managed_companies = [...(agency.managed_companies || []), action.payload.managed_company_id];
        }
        // selectedCompany'yi güncelleme - modal kendi state'ini yönetecek
      })
      .addCase(addAgencyClientRelation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // removeAgencyClientRelation
      .addCase(removeAgencyClientRelation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeAgencyClientRelation.fulfilled, (state, action) => {
        state.isLoading = false;
        // Agency company'yi güncelle
        const agency = state.companies.find(company => company.id === action.payload.agency_id);
        if (agency && agency.managed_companies?.includes(action.payload.managed_company_id)) {
          agency.managed_companies = agency.managed_companies.filter(id => id !== action.payload.managed_company_id);
        }
        // selectedCompany'yi güncelleme - modal kendi state'ini yönetecek
      })
      .addCase(removeAgencyClientRelation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { 
  setPage, 
  setLimit, 
  setFilters, 
  setViewMode, 
  clearError,
  clearSelectedCompany,
  clearClients
} = companiesSlice.actions;

export default companiesSlice.reducer;